'use client';

import { useState, useEffect } from "react";
import ConsoleLink from "@/components/ui/console-link";

type Plan = {
  name: string;
  description: string;
  monthlyPrice?: number;
  yearlyPrice?: number;
  features: string[];
  cta: string;
  ctaLocation: string;
};

type PricingToggleProps = {
  plans: {
    free: Plan;
    starter: Plan;
    enterprise: Plan;
  };
};

export function PricingToggle({ plans }: PricingToggleProps) {
  const [isYearly, setIsYearly] = useState(true);
  const [showContactEmail, setShowContactEmail] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const contactEmail = "hello@limbar.io";

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  return (
    <>
      <style jsx>{`
        @keyframes toastAnimation {
          0% {
            opacity: 0;
            transform: translate(-50%, 0);
          }
          10% {
            opacity: 1;
            transform: translate(-50%, -100%);
          }
          90% {
            opacity: 1;
            transform: translate(-50%, -100%);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -200%);
          }
        }
        .toast-animate {
          animation: toastAnimation 1s ease-in-out;
        }
      `}</style>
      <div className="flex justify-between items-center">
        <div className="mb-16">
          <h1 className="text-4xl font-bold text-[#1C1C1E]">
            Simple, transparent pricing
          </h1>
          <p className="text-2xl text-gray-500 mt-2">
            Start free. No credit card required.
          </p>
        </div>

        <div className="bg-gray-100 p-1.5 rounded-full">
          <button
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${
              !isYearly ? "bg-white shadow-sm text-[#1C1C1E]" : "text-gray-500"
            }`}
            onClick={() => setIsYearly(false)}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Monthly
          </button>
          <button
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${
              isYearly ? "bg-white shadow-sm text-[#1C1C1E]" : "text-gray-500"
            }`}
            onClick={() => setIsYearly(true)}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
              <path d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21M19 21H21M19 21H14M5 21H3M5 21H10M9 7H10M9 11H10M14 7H15M14 11H15M10 21V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V21M10 21H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Yearly
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {Object.entries(plans).map(([key, plan]) => (
          <div 
            key={key} 
            className="bg-white rounded-[16px] p-8 flex flex-col border border-gray-300/50 transition-all duration-200 hover:shadow-[0_0_50px_0_rgba(0,0,0,0.05)] hover:border-gray-300"
          >
            <div className="flex items-center gap-3 mb-6">
              <h3 className="text-2xl font-semibold text-[#1C1C1E]">{plan.name}</h3>
              {key === 'free' && (
                <span className="bg-[#175134] text-white px-3 py-1 rounded-full text-sm font-medium">
                  2 Hours Free Trial
                </span>
              )}
            </div>
            
            <p className="text-gray-600 text-sm mb-8">{plan.description}</p>
            
            <div className="mb-8">
              {plan.monthlyPrice !== undefined ? (
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-[#1C1C1E]">
                  ${isYearly && plan.yearlyPrice 
                    ? Math.round(plan.yearlyPrice / 12) 
                    : plan.monthlyPrice}
                </span>
                <span className="text-gray-500 text-sm">
                  /month
                </span>
              </div>
              ) : (
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-[#1C1C1E]">
                    Contact us
                  </span>
                </div>
              )}
              {isYearly && (plan.monthlyPrice ? (
                <p className="text-sm text-gray-500 mt-2">
                  Billed at <span className="line-through">${plan.monthlyPrice * 12}</span>{" "}
                  <span className="text-[#1C1C1E]">${plan.yearlyPrice}</span>/year
                </p>
              ) : (
                <p className="text-sm invisible mt-2">&nbsp;</p>
              ))}
            </div>

{key === 'enterprise' ? (
              showContactEmail ? (
                <div className="relative mb-8">
                  <input
                    type="text"
                    value={contactEmail}
                    readOnly
                    className="w-fit px-3 py-2 text-sm bg-gray-100 border-2 border-dashed border-gray-400 rounded-md focus:outline-none focus:ring-0 focus:border-gray-400 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={(e) => {
                      navigator.clipboard.writeText(contactEmail);
                      setShowToast(true);
                    }}
                    title="Click to copy email"
                  />
                  {showToast && (
                    <div
                      className="absolute left-1/2 px-2 py-1 bg-black text-white text-xs rounded toast-animate"
                      style={{ top: "0", transform: "translateX(-50%)" }}
                    >
                      Copied!
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowContactEmail(true)}
                  className="mb-8 w-fit bg-[#1C1C1E] text-white rounded-[16px] px-5 py-2.5 text-sm font-medium hover:bg-black transition-colors cursor-pointer"
                >
                  {plan.cta}
                </button>
              )
            ) : (
              <ConsoleLink
                location={plan.ctaLocation}
                className="mb-8 w-fit bg-[#1C1C1E] text-white rounded-[16px] px-5 py-2.5 text-sm font-medium hover:bg-black transition-colors"
              >
                {plan.cta}
              </ConsoleLink>
            )}

            <div>
              <div className="text-sm font-medium text-[#1C1C1E] mb-4">
                Plan Highlights
              </div>
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-gray-600">
                    <svg
                      className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </>
  );
} 